import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Eye, LogOut, Download, Search, Send, FileText } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { downloadQuotation } from '@/utils/quotationGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [rfqs, setRfqs] = useState([]);
  const [filteredRfqs, setFilteredRfqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [quotationData, setQuotationData] = useState({
    quoted_price: '',
    unit_price: '',
    delivery_timeline: '15-20 working days',
    payment_terms: '50% advance, 50% before dispatch',
    validity_days: 30,
    terms_conditions: `1. Prices are exclusive of GST (18% applicable)
2. Delivery timeline subject to receipt of advance payment
3. Standard warranty: 12 months from date of installation
4. Freight charges extra as applicable
5. Specifications as per customer requirements`,
    additional_notes: ''
  });
  const [quotations, setQuotations] = useState([]);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    const savedUsername = localStorage.getItem('admin_username');
    if (savedToken && savedUsername) {
      setToken(savedToken);
      setUsername(savedUsername);
      setIsAuthenticated(true);
      fetchRfqs(savedToken);
      fetchQuotations(savedToken);
    }
  }, []);

  useEffect(() => {
    filterRfqs();
  }, [rfqs, searchTerm, statusFilter]);

  const filterRfqs = () => {
    let filtered = [...rfqs];
    
    if (searchTerm) {
      filtered = filtered.filter(rfq => 
        rfq.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rfq.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rfq.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(rfq => rfq.status === statusFilter);
    }
    
    setFilteredRfqs(filtered);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      
      const { token, username: user } = response.data;
      setToken(token);
      setUsername(user);
      setIsAuthenticated(true);
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_username', user);
      
      fetchRfqs(token);
      toast.success('Login successful');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    setUsername('');
    setPassword('');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    toast.success('Logged out successfully');
  };

  const fetchRfqs = async (authToken) => {
    try {
      const response = await axios.get(`${API}/rfq`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      setRfqs(response.data);
    } catch (error) {
      console.error('Error fetching RFQs:', error);
      toast.error('Failed to fetch RFQs');
    }
  };

  const fetchQuotations = async (authToken) => {
    try {
      const response = await axios.get(`${API}/quotation`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    }
  };

  const handleSendQuotation = async () => {
    if (!quotationData.quoted_price || !quotationData.unit_price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        rfq_id: selectedRfq.id,
        quoted_price: parseFloat(quotationData.quoted_price),
        unit_price: parseFloat(quotationData.unit_price),
        delivery_timeline: quotationData.delivery_timeline,
        payment_terms: quotationData.payment_terms,
        validity_days: parseInt(quotationData.validity_days),
        terms_conditions: quotationData.terms_conditions,
        additional_notes: quotationData.additional_notes || null
      };

      const response = await axios.post(`${API}/quotation`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Download quotation as PDF
      toast.info('Generating PDF quotation...');
      try {
        await downloadQuotation(selectedRfq, response.data, token);
      } catch (pdfError) {
        console.error('PDF generation failed:', pdfError);
        // Continue even if PDF fails - quotation is already created
      }

      // Show appropriate success message based on email status
      if (response.data.email_sent) {
        toast.success('Quotation sent successfully! Email delivered to customer and PDF downloaded.');
      } else {
        toast.success('Quotation created and PDF downloaded! (Email not configured - manually send to customer)', {
          duration: 5000
        });
      }
      
      // Reset form
      setShowQuotationForm(false);
      setQuotationData({
        quoted_price: '',
        unit_price: '',
        delivery_timeline: '15-20 working days',
        payment_terms: '50% advance, 50% before dispatch',
        validity_days: 30,
        terms_conditions: `1. Prices are exclusive of GST (18% applicable)
2. Delivery timeline subject to receipt of advance payment
3. Standard warranty: 12 months from date of installation
4. Freight charges extra as applicable
5. Specifications as per customer requirements`,
        additional_notes: ''
      });

      // Refresh data
      await fetchRfqs(token);
      await fetchQuotations(token);
      
    } catch (error) {
      console.error('Error sending quotation:', error);
      toast.error('Failed to send quotation');
    } finally {
      setLoading(false);
    }
  };

  const openQuotationForm = (rfq) => {
    setSelectedRfq(rfq);
    
    // Auto-calculate prices
    const config = rfq.configuration;
    const quantity = config.quantity;
    
    // Simple pricing calculation
    let basePrice = 250;
    if (config.belt_type === 'V-Belts') basePrice = 180;
    else if (config.belt_type === 'Flat Belts') basePrice = 200;
    else if (config.belt_type === 'Conveyor Belts') basePrice = 350;
    else if (config.belt_type === 'Poly-V Belts') basePrice = 220;
    
    const areaFactor = (parseFloat(config.width) / 100) * (parseFloat(config.length) / 1000);
    const unitPrice = Math.round(basePrice * areaFactor);
    const subtotal = unitPrice * quantity;
    const totalWithGst = Math.round(subtotal * 1.18);
    
    setQuotationData(prev => ({
      ...prev,
      unit_price: unitPrice.toString(),
      quoted_price: totalWithGst.toString()
    }));
    
    setShowQuotationForm(true);
  };

  const updateRfqStatus = async (rfqId, newStatus) => {
    try {
      await axios.patch(
        `${API}/rfq/${rfqId}/status?status=${newStatus}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setRfqs(prev => prev.map(rfq => 
        rfq.id === rfqId ? { ...rfq, status: newStatus } : rfq
      ));
      
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const exportToPdf = (rfq) => {
    // Simple text export for now
    const content = `
RFQ DETAILS
-----------
ID: ${rfq.id}
Date: ${new Date(rfq.created_at).toLocaleDateString()}

COMPANY INFORMATION
-------------------
Company: ${rfq.company_name}
Contact: ${rfq.contact_person}
Email: ${rfq.email}
Phone: ${rfq.phone}
GST: ${rfq.gst_number || 'N/A'}
Delivery Location: ${rfq.delivery_location}
Required Date: ${rfq.required_delivery_date}

BELT CONFIGURATION
------------------
Type: ${rfq.configuration.belt_type}
Dimensions: ${rfq.configuration.thickness} × ${rfq.configuration.width} × ${rfq.configuration.length} mm
Plies: ${rfq.configuration.number_of_plies}
Colour: ${rfq.configuration.colour}
Surface: ${rfq.configuration.surface_type}
Temperature: ${rfq.configuration.temperature_range}
Oil/Chemical Resistance: ${rfq.configuration.oil_chemical_resistance ? 'Yes' : 'No'}
Quantity: ${rfq.configuration.quantity}

ADDITIONAL REMARKS
------------------
${rfq.additional_remarks || 'None'}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RFQ-${rfq.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('RFQ exported successfully');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="glass-card p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="heading-font text-3xl text-white mb-2">ADMIN LOGIN</h1>
            <p className="text-[#94A3B8] text-sm">SIMPSON & MUNRO Dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-white mb-2 block">Username</Label>
              <Input
                id="username"
                data-testid="admin-username-input"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-[#0F172A] border-[#334155] text-white"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-white mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-password-input"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0F172A] border-[#334155] text-white"
                required
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="btn-accent w-full py-6"
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <a 
              href="/admin/reset-password" 
              className="text-[#60A5FA] hover:text-[#93C5FD] text-sm"
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </a>
          </div>
          
          <div className="mt-4 p-4 bg-[#3B82F6]/10 border border-[#3B82F6]/20">
            <p className="text-[#94A3B8] text-xs text-center">
              Default credentials: admin / admin123
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="heading-font text-3xl text-[#1E293B] mb-2" data-testid="dashboard-title">
              RFQ DASHBOARD
            </h1>
            <p className="text-[#64748B] text-sm">Welcome back, {username}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="btn-secondary"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-6">
            <div className="text-2xl font-bold text-[#1E293B] mb-1">{rfqs.length}</div>
            <div className="text-[#64748B] text-sm">Total RFQs</div>
          </div>
          <div className="glass-card p-6">
            <div className="text-2xl font-bold text-[#F97316] mb-1">
              {rfqs.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-[#64748B] text-sm">Pending</div>
          </div>
          <div className="glass-card p-6">
            <div className="text-2xl font-bold text-[#3B82F6] mb-1">
              {rfqs.filter(r => r.status === 'processing').length}
            </div>
            <div className="text-[#64748B] text-sm">Processing</div>
          </div>
          <div className="glass-card p-6">
            <div className="text-2xl font-bold text-[#10B981] mb-1">
              {rfqs.filter(r => r.status === 'completed').length}
            </div>
            <div className="text-[#64748B] text-sm">Completed</div>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
              <Input
                placeholder="Search by company, contact, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white border-gray-300 text-[#1E293B] pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white border-gray-300 text-[#1E293B]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-300 text-[#1E293B]">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* RFQ Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="rfq-table">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Date</th>
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Company</th>
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Contact</th>
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Belt Type</th>
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Quantity</th>
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-[#64748B] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRfqs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-8 text-[#64748B]">
                      No RFQs found
                    </td>
                  </tr>
                ) : (
                  filteredRfqs.map((rfq) => (
                    <tr key={rfq.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`rfq-row-${rfq.id}`}>
                      <td className="p-4 text-[#1E293B] text-sm mono-font">
                        {rfq.created_at ? new Date(rfq.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4 text-[#1E293B] text-sm">{rfq.company_name || '-'}</td>
                      <td className="p-4 text-[#64748B] text-sm">{rfq.contact_person || '-'}</td>
                      <td className="p-4 text-[#1E293B] text-sm mono-font">{rfq.configuration?.belt_type || '-'}</td>
                      <td className="p-4 text-[#1E293B] text-sm">{rfq.configuration?.quantity || '-'}</td>
                      <td className="p-4">
                        <Select
                          value={rfq.status}
                          onValueChange={(val) => updateRfqStatus(rfq.id, val)}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs bg-[#0F172A] border-[#334155] text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0F172A] border-[#334155] text-white">
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedRfq(rfq)}
                            className="text-[#3B82F6] hover:text-[#2563EB] h-8 px-2"
                            data-testid={`view-rfq-${rfq.id}`}
                            title="View Details"
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openQuotationForm(rfq)}
                            className="text-[#10B981] hover:text-[#059669] h-8 px-2"
                            data-testid={`send-quote-${rfq.id}`}
                            title="Send Quotation"
                          >
                            <Send size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => exportToPdf(rfq)}
                            className="text-[#3B82F6] hover:text-[#2563EB] h-8 px-2"
                            data-testid={`export-rfq-${rfq.id}`}
                            title="Export RFQ"
                          >
                            <Download size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* RFQ Detail Dialog */}
      <Dialog open={!!selectedRfq} onOpenChange={() => setSelectedRfq(null)}>
        <DialogContent className="bg-[#0F172A] border-[#334155] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">RFQ DETAILS</DialogTitle>
          </DialogHeader>
          
          {selectedRfq && (
            <div className="space-y-6" data-testid="rfq-detail-modal">
              <div>
                <h3 className="text-[#3B82F6] font-semibold mb-3 text-sm">COMPANY INFORMATION</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Company</div>
                    <div className="text-white text-sm">{selectedRfq.company_name}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Contact Person</div>
                    <div className="text-white text-sm">{selectedRfq.contact_person}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Email</div>
                    <div className="text-white text-sm">{selectedRfq.email}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Phone</div>
                    <div className="text-white text-sm">{selectedRfq.phone}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">GST Number</div>
                    <div className="text-white text-sm">{selectedRfq.gst_number || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Delivery Location</div>
                    <div className="text-white text-sm">{selectedRfq.delivery_location}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-[#3B82F6] font-semibold mb-3 text-sm">BELT CONFIGURATION</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Belt Type</div>
                    <div className="text-white text-sm mono-font">{selectedRfq.configuration.belt_type}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Dimensions (mm)</div>
                    <div className="text-white text-sm mono-font">
                      {selectedRfq.configuration.thickness} × {selectedRfq.configuration.width} × {selectedRfq.configuration.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Plies</div>
                    <div className="text-white text-sm mono-font">{selectedRfq.configuration.number_of_plies}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Colour</div>
                    <div className="text-white text-sm">{selectedRfq.configuration.colour}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Surface Type</div>
                    <div className="text-white text-sm">{selectedRfq.configuration.surface_type}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Temperature Range</div>
                    <div className="text-white text-sm mono-font">{selectedRfq.configuration.temperature_range}</div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Oil/Chemical Resistance</div>
                    <div className="text-white text-sm">
                      {selectedRfq.configuration.oil_chemical_resistance ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#94A3B8] text-xs mb-1">Quantity</div>
                    <div className="text-white text-sm mono-font">{selectedRfq.configuration.quantity}</div>
                  </div>
                </div>
              </div>

              {selectedRfq.additional_remarks && (
                <div>
                  <h3 className="text-[#3B82F6] font-semibold mb-3 text-sm">ADDITIONAL REMARKS</h3>
                  <p className="text-[#94A3B8] text-sm">{selectedRfq.additional_remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quotation Form Dialog */}
      <Dialog open={showQuotationForm} onOpenChange={setShowQuotationForm}>
        <DialogContent className="bg-white border-gray-300 text-[#1E293B] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl text-[#1E293B]">SEND QUOTATION</DialogTitle>
            {selectedRfq && (
              <p className="text-[#64748B] text-sm">
                For: {selectedRfq.company_name} - {selectedRfq.configuration.belt_type}
              </p>
            )}
          </DialogHeader>
          
          {selectedRfq && (
            <div className="space-y-6">
              {/* Pricing Section */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-[#1E293B] font-semibold mb-4 text-sm">PRICING DETAILS</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="unit_price" className="text-[#1E293B] mb-2 block">Unit Price (₹) *</Label>
                    <Input
                      id="unit_price"
                      type="number"
                      step="0.01"
                      placeholder="Enter unit price"
                      value={quotationData.unit_price}
                      onChange={(e) => setQuotationData(prev => ({ ...prev, unit_price: e.target.value }))}
                      className="bg-white border-gray-300 text-[#1E293B]"
                      data-testid="unit-price-input"
                    />
                    <p className="text-xs text-[#64748B] mt-1">Price per single unit</p>
                  </div>

                  <div>
                    <Label htmlFor="quoted_price" className="text-[#1E293B] mb-2 block">Total Quoted Price (₹) *</Label>
                    <Input
                      id="quoted_price"
                      type="number"
                      step="0.01"
                      placeholder="Enter total price"
                      value={quotationData.quoted_price}
                      onChange={(e) => setQuotationData(prev => ({ ...prev, quoted_price: e.target.value }))}
                      className="bg-white border-gray-300 text-[#1E293B]"
                      data-testid="quoted-price-input"
                    />
                    <p className="text-xs text-[#64748B] mt-1">
                      For {selectedRfq.configuration.quantity} units (incl. GST)
                    </p>
                  </div>
                </div>
              </div>

              {/* Delivery & Payment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="delivery_timeline" className="text-[#1E293B] mb-2 block">Delivery Timeline *</Label>
                  <Input
                    id="delivery_timeline"
                    placeholder="e.g., 15-20 working days"
                    value={quotationData.delivery_timeline}
                    onChange={(e) => setQuotationData(prev => ({ ...prev, delivery_timeline: e.target.value }))}
                    className="bg-white border-gray-300 text-[#1E293B]"
                    data-testid="delivery-timeline-input"
                  />
                </div>

                <div>
                  <Label htmlFor="validity_days" className="text-[#1E293B] mb-2 block">Validity (Days) *</Label>
                  <Input
                    id="validity_days"
                    type="number"
                    placeholder="e.g., 30"
                    value={quotationData.validity_days}
                    onChange={(e) => setQuotationData(prev => ({ ...prev, validity_days: e.target.value }))}
                    className="bg-white border-gray-300 text-[#1E293B]"
                    data-testid="validity-days-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="payment_terms" className="text-[#1E293B] mb-2 block">Payment Terms *</Label>
                <Input
                  id="payment_terms"
                  placeholder="e.g., 50% advance, 50% before dispatch"
                  value={quotationData.payment_terms}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, payment_terms: e.target.value }))}
                  className="bg-white border-gray-300 text-[#1E293B]"
                  data-testid="payment-terms-input"
                />
              </div>

              {/* Terms & Conditions */}
              <div>
                <Label htmlFor="terms_conditions" className="text-[#1E293B] mb-2 block">Terms & Conditions *</Label>
                <Textarea
                  id="terms_conditions"
                  placeholder="Enter terms and conditions"
                  value={quotationData.terms_conditions}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                  className="bg-white border-gray-300 text-[#1E293B] h-32"
                  data-testid="terms-input"
                />
              </div>

              {/* Additional Notes */}
              <div>
                <Label htmlFor="additional_notes" className="text-[#1E293B] mb-2 block">Additional Notes (Optional)</Label>
                <Textarea
                  id="additional_notes"
                  placeholder="Any special notes or instructions"
                  value={quotationData.additional_notes}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, additional_notes: e.target.value }))}
                  className="bg-white border-gray-300 text-[#1E293B] h-24"
                  data-testid="notes-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => setShowQuotationForm(false)}
                  variant="outline"
                  className="btn-secondary flex-1"
                  data-testid="cancel-quotation-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendQuotation}
                  disabled={loading}
                  className="btn-accent flex-1"
                  data-testid="submit-quotation-btn"
                >
                  {loading ? 'Sending...' : 'Generate & Download Quotation'}
                </Button>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-xs text-[#3B82F6]">
                  📧 <strong>Auto Email Delivery:</strong> Quotation will be automatically emailed to: <strong>{selectedRfq.email}</strong><br/>
                  📄 A copy will also be downloaded to your computer for records.<br/>
                  💡 <strong>Note:</strong> To enable email sending, add your Resend API key to backend/.env
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
